import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import moment from 'moment';

import { PageHeader, Form, Input, Button, Switch, Cascader, Typography, Spin, message, DatePicker, InputNumber, Select, Badge } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';

import * as ToolRequest from '../../tools/ToolRequest';
import * as ToolPermission from '../../tools/ToolPermission';

import SpinFull from '../../components/SpinFull';

const formLayout = {
  labelCol: {
    sm: { span: 5 },
  },
  wrapperCol: {
    sm: { span: 16 },
  },
  labelWrap: true,
};

const formTailLayout = {
  wrapperCol: {
    sm: { offset: 5, span: 16 },
  },
};

const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

export default function FarmSeedlingRecordAddScreen(props) {
  const history = useHistory();
  const formRef = useRef(null);

  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);

  const [siteId, setSiteId] = useState(null);
  const [siteOptions, setSiteOptions] = useState([]);
  const [seedPurchases, setSeedPurchases] = useState([]);
  const [seedlingTrays, setSeedlingTrays] = useState([]);

  const { metadata } = props;

  useEffect(() => {
    (async () => {
      const sitesRes = await ToolRequest.request('GET', '/v1/farm-site', {
        count: 1000,
      });

      setSiteOptions(sitesRes.data.map(site => ({
        value: site.id,
        label: site.name,
      })));

      setLoaded(true);
    })();
  }, []);

  const onChangeSite = (siteId) => {
    setSiteId(siteId);
    searchSeedPurchase('');
    searchSeedlingTray('');
  };

  const searchSeedPurchase = (value) => {
    (async () => {
      const seedPurchasesRes = await ToolRequest.request('GET', '/v1/farm-seed-purchase', {
        siteId,
        count: 10,
        searchText: value !== '' ? `%${value}%` : undefined,
      });

      setSeedPurchases(seedPurchasesRes.data.sort((a, b) => {
        if (a.readableId < b.readableId) {
          return -1;
        } else if (a.readableId > b.readableId) {
          return 1;
        }
        return 0;
      }));
    })();
  }

  const searchSeedlingTray = (value) => {
    (async () => {
      const seedlingTrayRes = await ToolRequest.request('GET', '/v1/farm-seedling-tray', {
        siteId,
        count: 10,
        searchText: value !== '' ? `%${value}%` : undefined,
        inUse: false,
      });

      setSeedlingTrays(seedlingTrayRes.data.sort((a, b) => {
        if (a.readableId < b.readableId) {
          return -1;
        } else if (a.readableId > b.readableId) {
          return 1;
        }
        return 0;
      }));
    })();
  }

  const onFinish = async (values) => {
    setLoading(true);

    try {
      const res = await ToolRequest.request('POST', '/v1/farm-seedling-record', {
        siteId: values.siteId[0],
        seedPurchaseId: values.seedPurchaseId,
        seedlingTrayId: values.seedlingTrayId,
        amount: values.amount,
      });

      message.success('Create Seedling Record successfully');

      history.push('/farm-seedling-record');
    } catch (err) {
      setLoading(false);

      if (err?.response?.data) {
        switch (err.response.data) {
          case 'SEEDLING_TRAY_IN_USE': {
            return message.error('Seedling tray is in use');
          }
          default:
            return message.error('Unknown errors. Please retry');
        }
      }

      message.error('Unknown error. Please retry');
    }
  };

  return loaded ? (
    <PageHeader
      title="Add a Seedling Record"
      onBack={() => history.push('/farm-seedling-record')}
    >
      <Spin spinning={loading}>
        <Form
          {...formLayout}
          initialValues={{}}
          onFinish={onFinish}
          ref={formRef}
        >
          <Form.Item
            label="Site"
            name="siteId"
            rules={[
              {
                required: true,
                message: 'Please select a site',
              },
            ]}
          >
            <Cascader
              options={siteOptions}
              onChange={values => onChangeSite(values[0])}
              placeholder="Select Site"
              allowClear={false}
            />
          </Form.Item>

          <Form.Item
            label="Seed Purchase"
            name="seedPurchaseId"
            rules={[
              {
                required: true,
                message: 'Please select the seed purchase',
              },
            ]}
          >
            <Select
              showSearch
              filterOption={false}
              disabled={!siteId}
              placeholder="Select a seed purchase"
              onSearch={(value) => searchSeedPurchase(value)}
            >
              {seedPurchases.map(seedPurchase => (
                <Option key={seedPurchase.id} value={seedPurchase.value}>
                  {seedPurchase.readableId}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Seedling Tray"
            name="seedlingTrayId"
            rules={[
              {
                required: true,
                message: 'Please select the seedling tray',
              },
            ]}
          >
            <Select
              showSearch
              filterOption={false}
              disabled={!siteId}
              placeholder="Select a seedling tray"
              onSearch={(value) => searchSeedlingTray(value)}
              onChange={(value) => {
                const seedlingTraySelected = seedlingTrays.find(seedlingTray => seedlingTray.id === value);
                if (seedlingTraySelected && seedlingTraySelected.slotCount) {
                  formRef.current.setFieldsValue({
                    amount: seedlingTraySelected.slotCount,
                  });
                }
              }}
            >
              {seedlingTrays.map(seedlingTray => (
                <Option key={seedlingTray.id} value={seedlingTray.value}>
                  {seedlingTray.readableId}{seedlingTray.slotCount ? ` (${seedlingTray.slotCount})` : ''}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="No. of Seedlings"
            name="amount"
            rules={[
              {
                required: true,
                message: 'Please enter the no. of seedlings',
              },
            ]}
          >
            <InputNumber
              placeholder="1"
              min={1}
            />
          </Form.Item>

          <Form.Item {...formTailLayout}>
            <Button type="primary" htmlType="submit" block>
              Submit
            </Button>
          </Form.Item>
        </Form>
      </Spin>
    </PageHeader>
  ) : <SpinFull />;
};
